/* ============================================================
   Dathent — onboardingConnect.js (2026-09-08 fix-round, EPIC-029 follow-up)
   Pure, framework-free helpers pulled out of OnboardingActivation.jsx so
   the OAuth-return parsing and the connect-button capacity gate can be
   unit-tested without rendering the screen. No React, no fetch, no
   `window` — everything here takes plain values in and returns plain
   values out, mirroring lib/socialAddon.js's own pure/tested shape.
   ============================================================ */

/**
 * Extracts the OAuth-return signal fields auth-worker's redirect can carry
 * beyond `provider`/`connected` (which the caller reads separately since
 * they drive different UI — see OnboardingActivation.jsx's readOAuthReturn):
 *   - `error`      e.g. 'social_accounts_limit_reached', or any other
 *                  provider-side failure code.
 *   - `limit`      the org's connect-during-setup cap, appended alongside
 *                  `error=social_accounts_limit_reached` so the banner can
 *                  be worded with a real number instead of a guess.
 *   - `plan`       the org's plan key, appended the same way as `limit`.
 *   - `keptBrand`  '1' when a cross-org same-account reconnect was kept on
 *                  its existing brand instead of moving with the account.
 *
 * `params` is anything exposing `.get(key)` — a real `URLSearchParams` in
 * the app, a plain `URLSearchParams` (or stub with the same shape) in
 * tests. A missing/null `params` degrades to all-empty rather than
 * throwing, so a caller can pass a possibly-absent object safely.
 */
export function describeOAuthReturn(params) {
  const get = (key) => (params && typeof params.get === 'function' ? params.get(key) : null);
  const error = get('error') || null;
  const rawLimit = get('limit');
  const limit = rawLimit != null && rawLimit !== '' && !Number.isNaN(Number(rawLimit))
    ? Number(rawLimit)
    : null;
  const plan = get('plan') || null;
  const keptBrand = get('keptBrand') === '1';
  return { error, limit, plan, keptBrand };
}

/**
 * Mirrors Settings.jsx's AccountsTab `capacityBlocked` gate (~L973): a
 * Connect button is blocked ONLY when the live capacity is reached AND the
 * org is NOT in the billable 'priced' add-on mode. A 'priced' org sitting
 * at its included count may still connect as a paid Scheduler add-on
 * overage through the existing <SocialConnectConfirm> confirm-dialog path
 * (see OnboardingActivation's handleConnect) — that path must never be
 * disabled here, or the only way to pay for more channels during setup
 * disappears along with the button.
 *
 * `capacity` is `socialCapacityFrom(...)`'s return shape (reads `.reached`);
 * `addonState` is `resolveSocialAddonState(...)`'s return shape (reads
 * `.mode`). Either being absent/malformed fails closed to "not blocked" —
 * same fail-soft-to-off convention as the rest of the social-addon helpers
 * (lib/socialAddon.js), since a false negative here just re-surfaces the
 * hard 402 further down the connect flow instead of silently locking users
 * out of a control that should work.
 */
export function isConnectBlocked(capacity, addonState) {
  return Boolean(capacity?.reached) && addonState?.mode !== 'priced';
}
